import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  formatDate,
  formatEuro,
  isoToDateInput,
  parseDateToIso,
  parseEuroToCentimes,
} from '../lib/format';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { SummaryCard } from '../components/tresorerie/SummaryCard';

export function TresorerieGlobalWoodPage(): JSX.Element {
  const { data: summary, isLoading: loadingSummary } = useQuery({
    queryKey: ['dette-global-wood'],
    queryFn: () => api.detteGlobalWood.summary(),
  });
  const { data: paiements } = useQuery({
    queryKey: ['paiements-global-wood'],
    queryFn: () => api.paiementsGlobalWood.list(),
  });

  if (loadingSummary) return <p className="text-sm text-slate-500">Chargement…</p>;

  if (summary?.configured !== true) {
    return <InitForm />;
  }

  return <ConfiguredView summary={summary} paiementCount={paiements?.items.length ?? 0} />;
}

// ───────────────────────────────────────────────────────────────────────
// Formulaire d'initialisation
// ───────────────────────────────────────────────────────────────────────

function InitForm(): JSX.Element {
  const queryClient = useQueryClient();
  const [capital, setCapital] = useState('80000,00');
  const [dateDebut, setDateDebut] = useState(isoToDateInput(new Date().toISOString()));
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const ct = parseEuroToCentimes(capital);
      const dateIso = parseDateToIso(dateDebut);
      if (ct === null || ct <= 0) throw new Error('Capital invalide');
      if (dateIso === null) throw new Error('Date invalide');
      return api.detteGlobalWood.init({
        capital_initial_centimes: ct,
        date_debut: dateIso,
        notes: notes === '' ? null : notes,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dette-global-wood'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-tresorerie'] });
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Erreur'),
  });

  return (
    <div className="max-w-md space-y-4">
      <h1 className="text-2xl font-bold text-slate-100">Initialiser Global Wood</h1>
      <p className="text-sm text-slate-400">
        Capital initial financé par Global Wood. Une fois saisi, vous pourrez enregistrer les
        paiements et voir le restant dû calculé en temps réel.
      </p>
      <Input
        label="Capital initial (€)"
        value={capital}
        onChange={(e): void => setCapital(e.target.value)}
        placeholder="80 000,00"
      />
      <Input
        label="Date de début"
        type="date"
        value={dateDebut}
        onChange={(e): void => setDateDebut(e.target.value)}
      />
      <Input label="Notes" value={notes} onChange={(e): void => setNotes(e.target.value)} />
      {error !== null && <p className="text-xs text-red-400">{error}</p>}
      <Button
        onClick={(): void => {
          setError(null);
          mutation.mutate();
        }}
        disabled={mutation.isPending}
      >
        Initialiser
      </Button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Vue Global Wood configurée
// ───────────────────────────────────────────────────────────────────────

function ConfiguredView({
  summary,
  paiementCount,
}: {
  summary: NonNullable<Awaited<ReturnType<typeof api.detteGlobalWood.summary>>>;
  paiementCount: number;
}): JSX.Element {
  const [paiementOpen, setPaiementOpen] = useState(false);

  const { data: paiements } = useQuery({
    queryKey: ['paiements-global-wood'],
    queryFn: () => api.paiementsGlobalWood.list(),
  });

  return (
    <div className="max-w-5xl space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-slate-100">Dette Global Wood</h1>
        <p className="text-xs text-slate-500 mt-1">
          Capital initial {formatEuro(summary.dette?.capital_initial_centimes)} · {paiementCount}{' '}
          paiement{paiementCount > 1 ? 's' : ''} enregistré
          {paiementCount > 1 ? 's' : ''}
        </p>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard
          label="Capital initial"
          value={formatEuro(summary.dette?.capital_initial_centimes)}
        />
        <SummaryCard label="Total payé" value={formatEuro(summary.total_paye_centimes)} />
        <SummaryCard
          label="Restant dû"
          value={formatEuro(summary.restant_du_centimes)}
          tone="warning"
        />
        <SummaryCard
          label="Date soldage estimée"
          value={
            summary.date_soldage_estimee !== null
              ? formatDate(summary.date_soldage_estimee)
              : 'Indéterminée'
          }
          hint={
            summary.rythme_moyen_mensuel_centimes !== null
              ? `Rythme : ${formatEuro(summary.rythme_moyen_mensuel_centimes)}/mois`
              : undefined
          }
        />
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-200">Paiements</h2>
        <Button onClick={(): void => setPaiementOpen(true)}>Ajouter un paiement</Button>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/50 border-b border-slate-800">
            <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
              <th className="py-2 px-3">Date</th>
              <th className="py-2 px-3 text-right">Montant</th>
              <th className="py-2 px-3">Libellé</th>
              <th className="py-2 px-3">Container</th>
              <th className="py-2 px-3">Auteur</th>
            </tr>
          </thead>
          <tbody>
            {paiements?.items.map((p) => (
              <tr key={p.id} className="border-b border-slate-800/50">
                <td className="py-2 px-3">{formatDate(p.date_paiement)}</td>
                <td className="py-2 px-3 text-right">{formatEuro(p.montant_centimes)}</td>
                <td className="py-2 px-3">{p.libelle ?? '—'}</td>
                <td className="py-2 px-3">{p.container_reference ?? '—'}</td>
                <td className="py-2 px-3 text-slate-400">{p.created_by_username}</td>
              </tr>
            ))}
            {paiements?.items.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-8 text-sm text-slate-500">
                  Aucun paiement enregistré.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PaiementGWModal isOpen={paiementOpen} onClose={(): void => setPaiementOpen(false)} />
    </div>
  );
}

function PaiementGWModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(isoToDateInput(new Date().toISOString()));
  const [montant, setMontant] = useState('');
  const [libelle, setLibelle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const dateIso = parseDateToIso(date);
      const ct = parseEuroToCentimes(montant);
      if (dateIso === null) throw new Error('Date invalide');
      if (ct === null || ct <= 0) throw new Error('Montant invalide');
      return api.paiementsGlobalWood.create({
        container_id: null,
        date_paiement: dateIso,
        montant_centimes: ct,
        libelle: libelle === '' ? null : libelle,
        notes: null,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['paiements-global-wood'] });
      void queryClient.invalidateQueries({ queryKey: ['dette-global-wood'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-tresorerie'] });
      setMontant('');
      setLibelle('');
      onClose();
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Erreur'),
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Ajouter un paiement Global Wood"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={(): void => {
              setError(null);
              mutation.mutate();
            }}
            disabled={mutation.isPending}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Date"
          type="date"
          value={date}
          onChange={(e): void => setDate(e.target.value)}
        />
        <Input
          label="Montant (€)"
          value={montant}
          onChange={(e): void => setMontant(e.target.value)}
          placeholder="0,00"
        />
        <Input
          label="Libellé"
          value={libelle}
          onChange={(e): void => setLibelle(e.target.value)}
          placeholder="Virement mensuel"
        />
        {error !== null && <p className="text-xs text-red-400">{error}</p>}
      </div>
    </Modal>
  );
}
